import { Component, OnInit, ViewChild } from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { MatTabChangeEvent, MatTabGroup, MatTab } from "@angular/material/tabs";

import { DffCurrentRoute, DffRoute } from "@dffedb/router";
import { LocalStorage, trim } from "@dffedb/util";
import { HuskMigCredentialsService, LoginService } from "@e-forsyning/common/auth";
import { VaelgInstallationService } from "@e-forsyning/common/installation";
import { RedirectService } from "@e-forsyning/common/router";
import { getLoginRouteByPreference, isTeknikerContext } from "@e-forsyning/common/util";

import { LoginEmailComponent } from "./login-email/login-email.component";
import { LoginForbrugernrComponent } from "./login-forbrugernr/login-forbrugernr.component";
import { LoginFormBuilder } from "./login-form-builder";
import { LoginContainerComponent } from "./login-container/login-container.component";
import { HttpClient } from "@angular/common/http";
import { first } from "rxjs/operators";
import { BiometricService } from "./biometrics/biometrics.service";
import { MatButtonModule } from "@angular/material/button";
import { ToastService } from "@dffedb/ui/src/lib/toast/toast.service";

@Component({
    selector: "login",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.scss"],
    imports: [LoginContainerComponent, MatTabGroup, MatTab, LoginForbrugernrComponent, LoginEmailComponent, MatButtonModule]
})
export class LoginComponent implements OnInit {
    public form: UntypedFormGroup;
    public fejlBesked = "";
    public forbrugerNrArbejder = false;
    public emailArbejder = false;
    public dashboardStatus: { freetext: string; login: boolean; frontpage: boolean; status: string } = { freetext: "", login: false, frontpage: false, status: "" };

    public isBiometricAvailable = false;
    public biometricActionText = "";
    public biometricEnabled = false;
    public biometricLoginInProgress = false;
    public loginInputFilled: boolean = false;
    public passwordFilled: boolean = false;

    @ViewChild(MatTabGroup, { static: true }) public tabGroup: MatTabGroup;
    @ViewChild(LoginEmailComponent, { static: true }) public loginEmailComponent: LoginEmailComponent;
    @ViewChild(LoginForbrugernrComponent, { static: true }) public loginForbrugernrComponent: LoginForbrugernrComponent;

    constructor(
        private readonly currentRoute: DffCurrentRoute,
        private readonly huskMigCredentialsService: HuskMigCredentialsService,
        private readonly redirectService: RedirectService,
        private readonly localStorage: LocalStorage,
        private readonly loginService: LoginService,
        private readonly vaelgInstallationService: VaelgInstallationService,
        private readonly http: HttpClient,
        private readonly biometricService: BiometricService,
        private readonly toastService: ToastService
    ) {}

    public async ngOnInit(): Promise<void> {
        this.navigateToLoginPage();
        this.loginService.logout();
        this.form = LoginFormBuilder.opretForm(this.huskMigCredentialsService);
        let index = parseInt(this.localStorage.getItem("login.index") || "", 10);
        if (!!this.form.get("huskMig").value) {
            index = this.form.get("forbrugerNrGroup.forbrugerNr").value === null ? 1 : 0;
        }

        this.getWarningMessage();
        this.updateLoginInputFlags();
        await this.initBiometricStatus();
    }

    public navigateToLoginPage() {
        const loginPage = this.localStorage.getItem("sidste-login-side"); // "tekniker" | "forbruger" | null
        const loginTarget = getLoginRouteByPreference(loginPage, isTeknikerContext());

        // Hent aktuel path (uden hash og uden leading slash)
        const currentPath = trim(this.currentRoute.value.path, "/");
        const targetPath = trim(loginTarget, "/");

        // Hvis vi ikke allerede står på korrekt login-side, naviger derhen
        // (allow nested paths like /login/xyz to count as /login)
        if (!currentPath.startsWith(targetPath)) {
            this.redirectService.redirect(targetPath);
        }
    }

    public async onLoginSucceeded(): Promise<void> {
        sessionStorage.setItem("biometricAutoLoginDone", "true");
        await this.vaelgInstallationService.vaelgFoeresteInstallation(false, this.form.value.forbrugerNrGroup.forbrugerNr);
        this.redirect();
    }

    public redirect(): void {
        const { path, params } = this.getRedirectPath();
        if (path && path !== "404") {
            this.redirectService.redirect(path, params);
        } else {
            this.redirectService.redirectTilDefault();
        }
    }

    public onForbrugerNrAlphaKeyPressed(value: string): void {
        // Man har forsøgt at taste et ikke-nummerisk tal i feltet Forbrugernumme.
        // Det betyder sandsynligvis at man forsøger at logge ind med mail-adresse (nim@softvaerket.dk) eller som FOF-bruger (SN).
        // I stedet for at der ikke sker noget, forsøger vi at hjælpe ved at skifte til fanen hvor man kan logge ind med e-mail.
        this.form.get("emailGroup.email").setValue(value);
        this.tabGroup.selectedIndex = 1;
    }

    public onForbrugerNrArbejderChange(arbejder: boolean): void {
        this.forbrugerNrArbejder = arbejder;
    }

    public onEmailArbejderChange(arbejder: boolean): void {
        this.emailArbejder = arbejder;
    }

    private updateLoginInputFlags(): void {
        const email = this.form?.get("emailGroup.email")?.value;
        const password = this.form?.get("emailGroup.adgangskode")?.value;
        const technicianId = this.form?.get("forbrugerNrGroup.forbrugerNr")?.value;
        const technicianCode = this.form?.get("forbrugerNrGroup.kode")?.value;

        const formUserId = email || technicianId;
        const formSecret = password || technicianCode;

        this.loginInputFilled = !!formUserId;
        this.passwordFilled = !!formSecret;
    }

    public onSelectedTabChange(event: MatTabChangeEvent): void {
        this.focusTabContent(event.index);
    }

    private focusTabContent(tabIndex: number): void {
        if (tabIndex === 0) {
            this.loginForbrugernrComponent.focus();
        } else {
            this.loginEmailComponent.focus();
        }
    }

    private getRedirectPath(): DffRoute {
        // Fjern "login" fra url'en og returner resten.
        // Eksempel: /login/mine-oplysninger -> mine-oplysninger
        const { path, params } = this.currentRoute.value;
        const [_, ...rest] = trim(path, "/").split("/");
        return { params, path: rest.join("/") };
    }

    public getWarningMessage() {
        this.http.get<{ freetext: string; login: boolean; frontpage: boolean; status: string }>("/umbraco/api/DashboardApi/GetDashboardFields").subscribe({
            next: (data) => {
                this.dashboardStatus = data;
            },
            error: () => {
                // Umbraco utilgængelig, proxy nede eller request afbrudt under navigation – ingen global fejlstøj
            }
        });
    }

    private async initBiometricStatus(): Promise<void> {
        try {
            this.isBiometricAvailable = await this.biometricService.isAvailable();
            this.biometricEnabled = await this.biometricService.isBiometricLoginEnabled();
            this.biometricActionText = await this.biometricService.getBiometricActionText(this.biometricEnabled ? await this.biometricService.getBiometryType() : null);

            sessionStorage.getItem("biometricAutoLoginDone");
            if (!sessionStorage.getItem("biometricAutoLoginDone") && this.biometricEnabled) {
                sessionStorage.setItem("biometricAutoLoginDone", "true");
                setTimeout(() => {
                    this.loginUsingStoredCredentials();
                }, 200);
            }
        } catch {
            this.isBiometricAvailable = false;
            this.biometricEnabled = false;
        }
    }

    public async loginUsingStoredCredentials(): Promise<boolean | void> {
        try {
            const result = await this.biometricService.authenticate();
            if (!result) return false;

            this.fejlBesked = "";
            this.biometricLoginInProgress = true;

            this.loginService
                .login(result.userId, result.secret, false)
                .pipe(first())
                .subscribe({
                    next: async (res) => {
                        this.biometricLoginInProgress = false;
                        if (res.success) {
                            await this.onLoginSucceeded();
                        } else {
                            this.fejlBesked = "Login mislykkedes";
                        }
                    },
                    error: (err) => {
                        this.biometricLoginInProgress = false;
                        this.fejlBesked = err?.friendlyMessage || err?.message || "Login mislykkedes";
                    }
                });
        } catch (err: any) {
            this.biometricLoginInProgress = false;
            this.fejlBesked = err?.message ?? "Biometrisk autentificering mislykkedes";
            return false;
        }
    }

    public async biometricLogin(): Promise<void> {
        if (this.biometricLoginInProgress) return;

        this.fejlBesked = "";
        this.biometricLoginInProgress = true;

        try {
            const available = await this.biometricService.isAvailable();
            if (!available) {
                this.fejlBesked = "Biometrisk login virker ikke på denne enhed";
                this.biometricLoginInProgress = false;
                return;
            }

            const alreadyEnabled = await this.biometricService.isBiometricLoginEnabled();

            const email = this.form?.get("emailGroup.email")?.value;
            const password = this.form?.get("emailGroup.adgangskode")?.value;
            const technicianId = this.form?.get("forbrugerNrGroup.forbrugerNr")?.value;
            const technicianCode = this.form?.get("forbrugerNrGroup.kode")?.value;
            const formUserId = email || technicianId;
            const formSecret = password || technicianCode;

            if (!formUserId && !formSecret) {
                // Prøver at bruge gemte biometriske loginoplysninger
                if (this.biometricEnabled) {
                    const result = await this.biometricService.authenticate("Log ind med biometrisk godkendelse");
                    if (!result) {
                        this.fejlBesked = "Ingen biometriske loginoplysninger fundet";
                        this.biometricLoginInProgress = false;
                        return;
                    }

                    this.loginService
                        .login(result.userId, result.secret, false)
                        .pipe(first())
                        .subscribe({
                            next: async (res) => {
                                this.biometricLoginInProgress = false;
                                if (res.success) {
                                    await this.onLoginSucceeded();
                                } else {
                                    this.fejlBesked = "Login mislykkedes";
                                }
                            },
                            error: (err) => {
                                this.biometricLoginInProgress = false;
                                this.fejlBesked = err?.friendlyMessage || err?.message || "Login mislykkedes";
                            }
                        });
                    return;
                }

                // Hvis ingen oplysninger er fundet ses fejlbeskeden
                this.fejlBesked = `Udfyld venligst ${this.tabGroup.selectedIndex === 0 ? "forbrugernummer og kode" : "e-mail og adgangskode"} for at bruge biometrisk login.`;
                this.biometricLoginInProgress = false;
                return;
            }

            // CASE A: Formular udfyldt med forbrugerNr/email og adgangskode — login med disse oplysninger og spørg om gemme nye oplysninger.
            if (formUserId && formSecret) {
                this.loginService
                    .login(String(formUserId), String(formSecret), false)
                    .pipe(first())
                    .subscribe({
                        next: async (res) => {
                            this.biometricLoginInProgress = false;
                            if (res.success) {
                                try {
                                    const wantToStore = confirm(
                                        alreadyEnabled
                                            ? "Vil du opdatere nye loginoplysninger for hurtigere login næste gang? Tryk 'OK' og godkend med biometrisk godkendelse."
                                            : "Vil du gemme loginoplysninger for hurtigere login næste gang? Tryk 'OK' og godkend med biometrisk godkendelse."
                                    );
                                    if (wantToStore) {
                                        try {
                                            await this.biometricService.enableBiometricLogin(
                                                String(formUserId),
                                                String(formSecret),
                                                alreadyEnabled ? "Opdater biometriske loginoplysninger" : "Aktiver biometrisk login",
                                                alreadyEnabled ? "Opdater biometriske loginoplysninger" : "Aktiver biometrisk login",
                                                alreadyEnabled
                                                    ? "Bekræft din identitet for at opdatere dine gemte loginoplysninger sikkert."
                                                    : "Bekræft din identitet for at gemme dine loginoplysninger sikkert. Herefter kan du logge ind hurtigere med finger- eller ansigtsgenkendelse."
                                            );
                                            this.biometricEnabled = true;
                                            this.toastService.visInfo("Biometriske loginoplysninger " + (alreadyEnabled ? "opdateret." : "gemt."));
                                            await this.onLoginSucceeded();
                                        } catch (err) {
                                            alert("Kunne ikke gemme/opdatere biometriske loginoplysninger.");
                                            await this.onLoginSucceeded();
                                        }
                                    } else {
                                        await this.onLoginSucceeded();
                                    }
                                } catch {}
                            } else {
                                this.fejlBesked = "Login mislykkedes";
                            }
                        },
                        error: (err) => {
                            this.biometricLoginInProgress = false;
                            this.fejlBesked = err?.friendlyMessage || err?.message || "Login mislykkedes";
                        }
                    });
                return;
            }

            // CASE B: Ingen formularoplysninger — forsøg login med gemte biometriske oplysninger.
            const result = await this.biometricService.authenticate("Log ind med biometrisk godkendelse");
            if (!result) {
                this.fejlBesked = "Ingen biometriske loginoplysninger fundet";
                this.biometricLoginInProgress = false;
                return;
            }

            this.loginService
                .login(result.userId, result.secret, false)
                .pipe(first())
                .subscribe({
                    next: async (res) => {
                        this.biometricLoginInProgress = false;
                        if (res.success) {
                            await this.onLoginSucceeded();
                        } else {
                            this.fejlBesked = "Login mislykkedes";
                        }
                    },
                    error: (err) => {
                        this.biometricLoginInProgress = false;
                        this.fejlBesked = err?.friendlyMessage || err?.message || "Login mislykkedes";
                    }
                });
        } catch (err: any) {
            this.biometricLoginInProgress = false;
            const msg = String(err?.message || "").toLowerCase();
            if (msg.includes("cancel") || msg.includes("canceled") || msg.includes("user cancelled")) {
                this.fejlBesked = "Biometrisk login annulleret";
                return;
            }
            this.fejlBesked = err?.message ?? "Biometrisk autentificering mislykkedes";
            return;
        }
    }
}
